import { useQuery } from "@tanstack/react-query";
import {
	Shield,
	Package,
	Building2,
	GitBranch,
	AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import * as api from "@/services/api";

export function Dashboard() {
	const { data: features } = useQuery({
		queryKey: ["features"],
		queryFn: api.getFeatures,
	});

	const { data: roles } = useQuery({
		queryKey: ["roles"],
		queryFn: () => api.getRoles(),
	});

	const { data: plans } = useQuery({
		queryKey: ["plans"],
		queryFn: api.getPlans,
	});

	const { data: orgs } = useQuery({
		queryKey: ["organizations"],
		queryFn: api.getOrganizations,
	});

	const stats = [
		{
			label: "Features",
			value: features?.length ?? 0,
			icon: Package,
			color: "text-blue-600",
			bg: "bg-blue-100",
			to: "/permissions",
		},
		{
			label: "Roles",
			value: roles?.length ?? 0,
			icon: Shield,
			color: "text-purple-600",
			bg: "bg-purple-100",
			to: "/matrix",
		},
		{
			label: "Plans",
			value: plans?.length ?? 0,
			icon: GitBranch,
			color: "text-green-600",
			bg: "bg-green-100",
			to: "/packages",
		},
		{
			label: "Organizations",
			value: orgs?.length ?? 0,
			icon: Building2,
			color: "text-orange-600",
			bg: "bg-orange-100",
			to: "/bootstrap",
		},
	];

	return (
		<div className="space-y-8">
			<div>
				<h1 className="text-3xl font-bold text-gray-900">
					Authorization Dashboard
				</h1>
				<p className="text-gray-600 mt-2">
					Manage permissions, packages, and organization access control
				</p>
			</div>

			{/* Stats Grid */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
				{stats.map((stat) => (
					<Link key={stat.label} to={stat.to}>
						<Card className="hover:shadow-md transition-shadow cursor-pointer">
							<CardContent className="pt-6">
								<div className="flex items-center gap-4">
									<div className={`p-3 rounded-lg ${stat.bg}`}>
										<stat.icon className={`w-6 h-6 ${stat.color}`} />
									</div>
									<div>
										<p className="text-3xl font-bold">{stat.value}</p>
										<p className="text-sm text-muted-foreground">{stat.label}</p>
									</div>
								</div>
							</CardContent>
						</Card>
					</Link>
				))}
			</div>

			{/* Quick Actions */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				<Card>
					<CardHeader>
						<CardTitle>Quick Actions</CardTitle>
						<CardDescription>Common authorization tasks</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3">
						<Link to="/debugger">
							<Button variant="outline" className="w-full justify-start gap-2">
								<AlertTriangle className="w-4 h-4 text-yellow-600" />
								Debug 403 Error
							</Button>
						</Link>
						<Link to="/bootstrap">
							<Button variant="outline" className="w-full justify-start gap-2">
								<Building2 className="w-4 h-4 text-green-600" />
								Bootstrap New Org
							</Button>
						</Link>
						<Link to="/dependencies">
							<Button variant="outline" className="w-full justify-start gap-2">
								<GitBranch className="w-4 h-4 text-purple-600" />
								View Dependency Graph
							</Button>
						</Link>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>How Authorization Works</CardTitle>
						<CardDescription>Understanding the permission flow</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-4 text-sm">
							<div className="flex gap-3">
								<div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium text-xs">
									1
								</div>
								<div>
									<p className="font-medium">JWT Authentication</p>
									<p className="text-muted-foreground">
										User identity verified
									</p>
								</div>
							</div>
							<div className="flex gap-3">
								<div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium text-xs">
									2
								</div>
								<div>
									<p className="font-medium">Role Permission Check</p>
									<p className="text-muted-foreground">
										Role has feature:action?
									</p>
								</div>
							</div>
							<div className="flex gap-3">
								<div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium text-xs">
									3
								</div>
								<div>
									<p className="font-medium">Plan Feature Check</p>
									<p className="text-muted-foreground">
										Plan includes feature?
									</p>
								</div>
							</div>
							<div className="flex gap-3">
								<div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium text-xs">
									4
								</div>
								<div>
									<p className="font-medium">Data Scope Filter</p>
									<p className="text-muted-foreground">
										Org / Department / Self
									</p>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
